import { useEffect } from "react";

/**
 * SEO Component Props
 */
export interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: "website" | "product" | "article";
  noindex?: boolean;
  product?: {
    price: number;
    currency?: string;
    availability?: "InStock" | "OutOfStock" | "PreOrder";
    sku?: string;
  };
  faq?: Array<{ question: string; answer: string }>;
  organization?: {
    name: string;
    logo?: string;
    url?: string;
  };
  breadcrumbs?: Array<{ name: string; url: string }>;
}

const SITE_NAME = "Zest and Zeal";
const DEFAULT_IMAGE = "/og-image.jpg";
const SITE_URL = typeof window !== "undefined" ? window.location.origin : "";

/**
 * Helper to set or update a meta tag
 */
function setMetaTag(name: string, content: string, isProperty = false) {
  const attribute = isProperty ? "property" : "name";
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.setAttribute("content", content);
}

/**
 * Helper to set or update a link tag
 */
function setLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  
  element.setAttribute("href", href);
}

/**
 * Helper to add or update JSON-LD script
 */
function setJsonLd(id: string, data: object) {
  let script = document.querySelector(`script#${id}`);
  
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.setAttribute("type", "application/ld+json");
    document.head.appendChild(script);
  }
  
  script.textContent = JSON.stringify(data);
}

/**
 * Helper to remove JSON-LD script
 */
function removeJsonLd(id: string) {
  const script = document.querySelector(`script#${id}`);
  if (script) {
    script.remove();
  }
}

/**
 * SEO Component - Manages document head meta tags and structured data
 * Compatible with React 19+
 */
export function SEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  noindex = false,
  product,
  faq,
  organization,
  breadcrumbs,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = `${title} | ${SITE_NAME}`;
    const fullImage = image?.startsWith("http") ? image : `${SITE_URL}${image || DEFAULT_IMAGE}`;
    const fullCanonical = canonical ? `${SITE_URL}${canonical}` : window.location.href;

    // Set document title
    document.title = fullTitle;

    // Basic meta tags
    setMetaTag("description", description);
    
    // Robots
    if (noindex) {
      setMetaTag("robots", "noindex, nofollow");
    } else {
      setMetaTag("robots", "index, follow");
    }

    // Canonical URL
    setLinkTag("canonical", fullCanonical);

    // Open Graph tags
    setMetaTag("og:title", fullTitle, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:type", type, true);
    setMetaTag("og:url", fullCanonical, true);
    setMetaTag("og:image", fullImage, true);
    setMetaTag("og:site_name", SITE_NAME, true);

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", fullTitle);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", fullImage);

    // Product-specific Open Graph tags
    if (product) {
      setMetaTag("og:type", "product", true);
      setMetaTag("product:price:amount", String(product.price), true);
      setMetaTag("product:price:currency", product.currency || "NGN", true);
      if (product.availability) {
        setMetaTag("product:availability", product.availability, true);
      }
    }

    // Structured Data: Product
    if (product) {
      setJsonLd("product-jsonld", {
        "@context": "https://schema.org",
        "@type": "Product",
        name: title,
        description: description,
        image: fullImage,
        offers: {
          "@type": "Offer",
          price: product.price,
          priceCurrency: product.currency || "NGN",
          availability: `https://schema.org/${product.availability || "InStock"}`,
          ...(product.sku && { sku: product.sku }),
        },
      });
    } else {
      removeJsonLd("product-jsonld");
    }

    // Structured Data: FAQ
    if (faq && faq.length > 0) {
      setJsonLd("faq-jsonld", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      });
    } else {
      removeJsonLd("faq-jsonld");
    }

    // Structured Data: Organization
    if (organization) {
      setJsonLd("organization-jsonld", {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: organization.name,
        url: organization.url || SITE_URL,
        ...(organization.logo && { logo: organization.logo }),
      });
    } else {
      removeJsonLd("organization-jsonld");
    }

    // Structured Data: Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      setJsonLd("breadcrumb-jsonld", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: crumb.name,
          item: `${SITE_URL}${crumb.url}`,
        })),
      });
    } else {
      removeJsonLd("breadcrumb-jsonld");
    }

    // Cleanup function
    return () => {
      // Remove product-specific meta tags on unmount
      if (product) {
        const productMetas = document.querySelectorAll('meta[property^="product:"]');
        productMetas.forEach((meta) => meta.remove());
      }
    };
  }, [title, description, canonical, image, type, noindex, product, faq, organization, breadcrumbs]);

  // This component doesn't render anything
  return null;
}

export default SEO;
